'use client';

/**
 * DESIGN.md §8.1 / §8.5 — pixelation overlay.
 *
 * Replaces the original brute-force grid of ~84 absolutely-positioned
 * divs (each running `backdrop-filter: blur(37px) mix-blend-overlay`)
 * with a single inline `<svg>` containing one `<pattern>` whose tile
 * is a single `<rect>`. The SVG fills its container (`width="100%"
 * height="100%"`) and the pattern auto-tiles across it — adding a
 * grid cell is a no-op for the DOM, only the SVG renderer does
 * fragment work.
 *
 * Sizing: a ResizeObserver measures the container's height and sets
 * `cellSize = height / TARGET_ROWS` so EXACTLY `TARGET_ROWS` rows
 * fit vertically with no leftover strip (DESIGN.md ask). The width
 * tiles to whatever the container is — partial cells at the right
 * edge are masked by `overflow-hidden`.
 *
 * Decorative — `pointer-events: none` and `aria-hidden`.
 */
import { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/cn.js';

export type PixelationGridVariant = 'hero-right' | 'cta-full';

export interface PixelationGridProps {
  variant?: PixelationGridVariant;
  className?: string;
}

/**
 * How many rows fit vertically inside the container, regardless of
 * its actual height. Hero is the dense pixel grid on the right half
 * of the hero (§8.1); CTA covers the full section (§8.5) and uses
 * fewer/larger cells.
 */
const TARGET_ROWS: Record<PixelationGridVariant, number> = {
  'hero-right': 15,
  'cta-full': 10,
};

const POSITIONS: Record<PixelationGridVariant, string> = {
  'hero-right': 'right-0 top-0 w-1/2 h-[960px]',
  'cta-full': 'inset-0 w-full h-full',
};

export function PixelationGrid({ variant = 'hero-right', className }: PixelationGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [cellSize, setCellSize] = useState(64);

  useEffect(() => {
    // The measured div is part of every render, so it is mounted when this effect runs.
    const el = containerRef.current as HTMLDivElement;
    const measure = () => {
      const h = el.clientHeight;
      if (h <= 0) return;
      // Fractional cell sizes are fine — SVG renders subpixel rects
      // cleanly enough at decoration densities, and an exact divide
      // keeps the bottom edge flush with the container.
      const next = h / TARGET_ROWS[variant];
      setCellSize((prev) => (Math.abs(prev - next) < 0.5 ? prev : next));
    };
    measure();
    const obs = new ResizeObserver(measure);
    obs.observe(el);
    return () => obs.disconnect();
  }, [variant]);

  const patternId = `pixelation-${variant}`;

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      className={cn('pointer-events-none absolute overflow-hidden', POSITIONS[variant], className)}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
        preserveAspectRatio="none"
        aria-hidden="true"
        focusable="false"
        role="presentation"
        style={{ mixBlendMode: 'overlay' }}
      >
        <defs>
          <pattern id={patternId} patternUnits="userSpaceOnUse" width={cellSize} height={cellSize}>
            <rect
              x={0}
              y={0}
              width={cellSize}
              height={cellSize}
              fill="white"
              fillOpacity={0.04}
              stroke="white"
              strokeOpacity={0.12}
              strokeWidth={1}
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${patternId})`} />
      </svg>
    </div>
  );
}
